import { Component, ElementRef, ViewChild, Input, Output, EventEmitter, AfterViewInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { IonModal, IonIcon } from '@ionic/angular/standalone';
import { SexyModule } from "@qid/sexy";
import { SuperButtonComponent } from "@qid/super";

@Component({
    selector: 'custom-image-cropper',
    templateUrl: './custom-image-cropper.component.html',
    styleUrls: ['./custom-image-cropper.component.scss'],
    standalone: true,
    imports: [CommonModule, SexyModule, SuperButtonComponent, IonModal, IonIcon]
})
export class CustomImageCropperComponent implements AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('canvas', { static: false }) canvas: ElementRef<HTMLCanvasElement>;

    @Input() imageUrl: string = '';
    @Input() isOpen: boolean = false;
    @Input() title: string = 'Image';
    @Output() croppedImageUrl = new EventEmitter<string>();
    @Output() cancel = new EventEmitter<void>();

    private ctx: CanvasRenderingContext2D;
    private image = new Image();
    private cropArea = {
        x: 0,
        y: 0,
        width: 0,
        height: 0
    };
    private imagePosition = {
        x: 0,
        y: 0
    };
    private isDragging = false;
    private isResizingHeight = false;
    private isResizingWidth = false;
    private dragStart = { x: 0, y: 0 };
    private resizeHandle = '';
    private imageLoaded = false;
    private lastMousePosition = { x: 0, y: 0 };
    private animationFrameId: number = null;
    private isMouseDown = false;
    private rotationAngle = 0; // Rotation angle in degrees
    public showPreview = false; // Preview mode state
    public previewImageUrl = ''; // Preview image URL

    // Cached scale calculations for performance
    private cachedScale: number = 0;
    private cachedScaledWidth: number = 0;
    private cachedScaledHeight: number = 0;

    private async triggerHapticFeedback() {
        if (Capacitor.isNativePlatform()) {
            try {
                await Haptics.impact({ style: ImpactStyle.Light });
            } catch (error) {
                // Silently handle haptic feedback errors
                console.debug('Haptic feedback not available:', error);
            }
        }
    }

    rotateLeft() {
        this.rotationAngle = (this.rotationAngle - 90) % 360;
        if (this.rotationAngle < 0) {
            this.rotationAngle += 360;
        }
        this.triggerHapticFeedback();
        this.draw();
    }

    rotateRight() {
        this.rotationAngle = (this.rotationAngle + 90) % 360;
        this.triggerHapticFeedback();
        this.draw();
    }

    ngAfterViewInit() {
        // Canvas will be initialized when modal opens
        // Check if modal is already open
        if (this.isOpen && this.canvas?.nativeElement) {
            this.initializeCanvas();
        }
    }

    private initializeCanvas() {
        if (!this.canvas?.nativeElement) return;

        this.ctx = this.canvas.nativeElement.getContext('2d');
        // Enable high-quality image smoothing for better display quality
        if (this.ctx) {
            this.ctx.imageSmoothingEnabled = true;
            this.ctx.imageSmoothingQuality = 'high';
        }
        // Delay canvas setup to ensure modal is fully rendered
        setTimeout(() => {
            this.setupCanvas();
            if (this.imageUrl) {
                this.loadImage();
            }
        }, 100);
    }

    ngOnChanges(changes: SimpleChanges) {
        // Reset preview state when image URL changes
        if (changes['imageUrl'] && !changes['imageUrl'].firstChange) {
            this.showPreview = false;
            this.previewImageUrl = '';
            if (this.ctx) {
                setTimeout(() => {
                    this.setupCanvas();
                    this.loadImage();
                }, 100);
            }
        }
        // Initialize canvas when modal opens
        if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
            this.showPreview = false;
            this.previewImageUrl = '';
            // Clear cached scale when opening modal
            this.cachedScale = 0;
            // Wait for DOM to render the canvas element
            setTimeout(() => {
                this.initializeCanvas();
            }, 150);
        }
    }

    private setupCanvas() {
        if (!this.canvas?.nativeElement) return;

        const canvas = this.canvas.nativeElement;
        const container = canvas.parentElement;

        if (container && container.clientWidth > 0 && container.clientHeight > 0) {
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
        } else {
            // Fallback to default dimensions if container is not ready
            canvas.width = 400;
            canvas.height = 300;
        }
        // Clear cached scale when canvas resizes
        this.cachedScale = 0;
    }

    private loadImage() {
        this.image.onload = () => {
            this.imageLoaded = true;
            this.fitImageToCanvas();
            this.draw();
        };
        this.image.onerror = () => {
            this.imageLoaded = false;
        };
        this.image.src = this.imageUrl;
    }

    private fitImageToCanvas() {
        const canvas = this.canvas.nativeElement;
        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;
        const imageWidth = this.image.width;
        const imageHeight = this.image.height;

        // Calculate scale to fit image within canvas while maintaining aspect ratio
        const scaleX = canvasWidth / imageWidth;
        const scaleY = canvasHeight / imageHeight;
        this.cachedScale = Math.min(scaleX, scaleY);

        // Calculate scaled image dimensions
        this.cachedScaledWidth = imageWidth * this.cachedScale;
        this.cachedScaledHeight = imageHeight * this.cachedScale;

        // Set crop area to ID card aspect ratio (1.586:1) by default, centered
        const idCardAspectRatio = 1.586; // Standard ID card ratio
        const cropWidth = Math.min(this.cachedScaledWidth * 0.9, canvasWidth * 0.9); // Initial size: 90% of available space
        const cropHeight = cropWidth / idCardAspectRatio;

        // Center the crop area
        const cropX = (canvasWidth - cropWidth) / 2;
        const cropY = (canvasHeight - cropHeight) / 2;

        this.cropArea = {
            x: cropX,
            y: cropY,
            width: cropWidth,
            height: cropHeight
        };

        // Initialize image position to center the image within the crop area
        this.imagePosition = {
            x: (canvasWidth - this.cachedScaledWidth) / 2,
            y: (canvasHeight - this.cachedScaledHeight) / 2
        };
    }

    private draw() {
        if (!this.imageLoaded) return;

        // Cancel any pending animation frame
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }

        // Use requestAnimationFrame for smoother rendering
        this.animationFrameId = requestAnimationFrame(() => {
            const canvas = this.canvas.nativeElement;
            this.ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Draw crop area overlay (which includes the image)
            this.drawCropArea();
            this.animationFrameId = null;
        });
    }

    private drawCropArea() {
        const canvas = this.canvas.nativeElement;

        // Use cached scale or calculate if not cached
        if (this.cachedScale === 0) {
            const imageWidth = this.image.width;
            const imageHeight = this.image.height;
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const scaleX = canvasWidth / imageWidth;
            const scaleY = canvasHeight / imageHeight;
            this.cachedScale = Math.min(scaleX, scaleY);
            this.cachedScaledWidth = imageWidth * this.cachedScale;
            this.cachedScaledHeight = imageHeight * this.cachedScale;
        }

        const scaledWidth = this.cachedScaledWidth;
        const scaledHeight = this.cachedScaledHeight;

        // Save the current context state
        this.ctx.save();

        // Apply rotation transformation
        if (this.rotationAngle !== 0) {
            const centerX = this.imagePosition.x + scaledWidth / 2;
            const centerY = this.imagePosition.y + scaledHeight / 2;
            this.ctx.translate(centerX, centerY);
            this.ctx.rotate((this.rotationAngle * Math.PI) / 180);
            this.ctx.translate(-centerX, -centerY);
        }

        // First, draw the full image at the current position
        this.ctx.drawImage(this.image, this.imagePosition.x, this.imagePosition.y, scaledWidth, scaledHeight);

        // Restore the context state
        this.ctx.restore();

        // Then, draw the semi-transparent overlay over the entire canvas
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        this.ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Save the current context state
        this.ctx.save();

        // Set clipping region to crop area
        this.ctx.beginPath();
        this.ctx.rect(this.cropArea.x, this.cropArea.y, this.cropArea.width, this.cropArea.height);
        this.ctx.clip();

        // Apply rotation transformation for the crop area
        if (this.rotationAngle !== 0) {
            const centerX = this.imagePosition.x + scaledWidth / 2;
            const centerY = this.imagePosition.y + scaledHeight / 2;
            this.ctx.translate(centerX, centerY);
            this.ctx.rotate((this.rotationAngle * Math.PI) / 180);
            this.ctx.translate(-centerX, -centerY);
        }

        // Redraw the image in the crop area to ensure it's visible
        this.ctx.drawImage(this.image, this.imagePosition.x, this.imagePosition.y, scaledWidth, scaledHeight);

        // Restore the context state
        this.ctx.restore();

        // Draw crop area border with rounded corners
        this.ctx.strokeStyle = '#ffffff';
        this.ctx.lineWidth = 2;
        this.drawRoundedRect(this.cropArea.x, this.cropArea.y, this.cropArea.width, this.cropArea.height, 16);

        // Draw height adjustment bars
        this.drawHeightAdjustmentBars();

        // Draw width adjustment bars
        this.drawWidthAdjustmentBars();
    }

    private drawRoundedRect(x: number, y: number, width: number, height: number, radius: number) {
        this.ctx.beginPath();
        this.ctx.moveTo(x + radius, y);
        this.ctx.lineTo(x + width - radius, y);
        this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        this.ctx.lineTo(x + width, y + height - radius);
        this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        this.ctx.lineTo(x + radius, y + height);
        this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        this.ctx.lineTo(x, y + radius);
        this.ctx.quadraticCurveTo(x, y, x + radius, y);
        this.ctx.closePath();
        this.ctx.stroke();
    }

    private drawHeightAdjustmentBars() {
        const barWidth = 60;
        const barHeight = 16;

        // Calculate center position for bars
        const centerX = this.cropArea.x + this.cropArea.width / 2;

        // Top edge bar - centered on the top edge
        const topY = this.cropArea.y - (barHeight / 2);
        const topBarX = centerX - (barWidth / 2);

        // Check if top bar is active
        const isTopActive = this.isResizingHeight && this.resizeHandle === 'top';

        this.ctx.fillStyle = '#ffffff';
        this.ctx.strokeStyle = isTopActive ? '#2196F3' : '#e0e0e0';
        this.ctx.lineWidth = isTopActive ? 2 : 1;

        this.drawRoundedRect(topBarX, topY, barWidth, barHeight, 8);
        this.ctx.fill();
        this.ctx.stroke();

        // Bottom edge bar - centered on the bottom edge
        const bottomY = this.cropArea.y + this.cropArea.height - (barHeight / 2);
        const bottomBarX = centerX - (barWidth / 2);

        // Check if bottom bar is active
        const isBottomActive = this.isResizingHeight && this.resizeHandle === 'bottom';

        this.ctx.fillStyle = '#ffffff';
        this.ctx.strokeStyle = isBottomActive ? '#2196F3' : '#e0e0e0';
        this.ctx.lineWidth = isBottomActive ? 2 : 1;

        this.drawRoundedRect(bottomBarX, bottomY, barWidth, barHeight, 8);
        this.ctx.fill();
        this.ctx.stroke();
    }

    private drawWidthAdjustmentBars() {
        const barWidth = 16;
        const barHeight = 60;

        // Calculate center position for bars
        const centerY = this.cropArea.y + this.cropArea.height / 2;

        // Left edge bar - centered on the left edge
        const leftX = this.cropArea.x - (barWidth / 2);
        const leftBarY = centerY - (barHeight / 2);

        // Check if left bar is active
        const isLeftActive = this.isResizingWidth && this.resizeHandle === 'left';

        this.ctx.fillStyle = '#ffffff';
        this.ctx.strokeStyle = isLeftActive ? '#2196F3' : '#e0e0e0';
        this.ctx.lineWidth = isLeftActive ? 2 : 1;

        this.drawRoundedRect(leftX, leftBarY, barWidth, barHeight, 8);
        this.ctx.fill();
        this.ctx.stroke();

        // Right edge bar - centered on the right edge
        const rightX = this.cropArea.x + this.cropArea.width - (barWidth / 2);
        const rightBarY = centerY - (barHeight / 2);

        // Check if right bar is active
        const isRightActive = this.isResizingWidth && this.resizeHandle === 'right';

        this.ctx.fillStyle = '#ffffff';
        this.ctx.strokeStyle = isRightActive ? '#2196F3' : '#e0e0e0';
        this.ctx.lineWidth = isRightActive ? 2 : 1;

        this.drawRoundedRect(rightX, rightBarY, barWidth, barHeight, 8);
        this.ctx.fill();
        this.ctx.stroke();
    }


    onMouseDown(event: MouseEvent) {
        const rect = this.canvas.nativeElement.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        this.isMouseDown = true;
        this.lastMousePosition = { x, y };

        // Check if clicking on edges for resizing
        const edgeThreshold = 20; // Increased threshold for better UX with larger bars

        // Check top edge for height resizing
        if (x >= this.cropArea.x && x <= this.cropArea.x + this.cropArea.width &&
            y >= this.cropArea.y - edgeThreshold && y <= this.cropArea.y + edgeThreshold) {
            this.isResizingHeight = true;
            this.resizeHandle = 'top';
            this.dragStart = { x, y };
            this.triggerHapticFeedback();
            event.preventDefault();
            return;
        }

        // Check bottom edge for height resizing
        if (x >= this.cropArea.x && x <= this.cropArea.x + this.cropArea.width &&
            y >= this.cropArea.y + this.cropArea.height - edgeThreshold && y <= this.cropArea.y + this.cropArea.height + edgeThreshold) {
            this.isResizingHeight = true;
            this.resizeHandle = 'bottom';
            this.dragStart = { x, y };
            this.triggerHapticFeedback();
            event.preventDefault();
            return;
        }

        // Check left edge for width resizing
        if (x >= this.cropArea.x - edgeThreshold && x <= this.cropArea.x + edgeThreshold &&
            y >= this.cropArea.y && y <= this.cropArea.y + this.cropArea.height) {
            this.isResizingWidth = true;
            this.resizeHandle = 'left';
            this.dragStart = { x, y };
            this.triggerHapticFeedback();
            event.preventDefault();
            return;
        }

        // Check right edge for width resizing
        if (x >= this.cropArea.x + this.cropArea.width - edgeThreshold && x <= this.cropArea.x + this.cropArea.width + edgeThreshold &&
            y >= this.cropArea.y && y <= this.cropArea.y + this.cropArea.height) {
            this.isResizingWidth = true;
            this.resizeHandle = 'right';
            this.dragStart = { x, y };
            this.triggerHapticFeedback();
            event.preventDefault();
            return;
        }

        // If clicking anywhere else, start panning the image
        this.isDragging = true;
        this.dragStart = { x: x - this.imagePosition.x, y: y - this.imagePosition.y };
        event.preventDefault();
    }

    onMouseMove(event: MouseEvent) {
        if (!this.isMouseDown) return;

        const rect = this.canvas.nativeElement.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        // Calculate delta for smoother movement
        const deltaX = x - this.lastMousePosition.x;
        const deltaY = y - this.lastMousePosition.y;

        if (this.isResizingHeight) {
            this.resizeHeight(y);
        } else if (this.isResizingWidth) {
            this.resizeWidth(x);
        } else if (this.isDragging) {
            // Smooth panning with delta movement
            this.imagePosition.x += deltaX;
            this.imagePosition.y += deltaY;

            // Keep image within reasonable bounds
            if (this.cachedScale === 0) {
                const canvas = this.canvas.nativeElement;
                const imageWidth = this.image.width;
                const imageHeight = this.image.height;
                const scaleX = canvas.width / imageWidth;
                const scaleY = canvas.height / imageHeight;
                this.cachedScale = Math.min(scaleX, scaleY);
                this.cachedScaledWidth = imageWidth * this.cachedScale;
                this.cachedScaledHeight = imageHeight * this.cachedScale;
            }

            const canvas = this.canvas.nativeElement;
            const padding = 50;
            this.imagePosition.x = Math.max(-this.cachedScaledWidth + padding, Math.min(canvas.width - padding, this.imagePosition.x));
            this.imagePosition.y = Math.max(-this.cachedScaledHeight + padding, Math.min(canvas.height - padding, this.imagePosition.y));
        }

        // Update last mouse position
        this.lastMousePosition = { x, y };

        // Throttle drawing for better performance
        this.draw();
    }

    onMouseUp() {
        this.isDragging = false;
        this.isResizingHeight = false;
        this.isResizingWidth = false;
        this.resizeHandle = '';
        this.isMouseDown = false;
    }

    onTouchStart(event: TouchEvent) {
        event.preventDefault();
        if (event.touches.length === 1) {
            const touch = event.touches[0];
            const mouseEvent = new MouseEvent('mousedown', {
                clientX: touch.clientX,
                clientY: touch.clientY
            });
            this.onMouseDown(mouseEvent);
        }
    }

    onTouchMove(event: TouchEvent) {
        event.preventDefault();
        if (event.touches.length === 1) {
            const touch = event.touches[0];
            const mouseEvent = new MouseEvent('mousemove', {
                clientX: touch.clientX,
                clientY: touch.clientY
            });
            this.onMouseMove(mouseEvent);
        }
    }

    onTouchEnd(event: TouchEvent) {
        event.preventDefault();
        this.onMouseUp();
    }

    private resizeHeight(y: number) {
        const canvas = this.canvas.nativeElement;
        const minHeight = 50; // Minimum crop area height
        const maxHeight = canvas.height * 0.9; // Maximum 90% of canvas height

        let newHeight: number;

        if (this.resizeHandle === 'top') {
            // Resize from top - adjust height and move position to keep center
            const heightChange = (this.dragStart.y - y) * 0.5; // Reduce sensitivity by 50%
            newHeight = this.cropArea.height + heightChange;

            if (newHeight >= minHeight && newHeight <= maxHeight) {
                const centerY = this.cropArea.y + this.cropArea.height / 2;
                this.cropArea.height = newHeight;
                this.cropArea.y = centerY - this.cropArea.height / 2;

                // Update drag start to prevent accumulation
                this.dragStart.y = y;
            }
        } else if (this.resizeHandle === 'bottom') {
            // Resize from bottom - adjust height only
            const heightChange = (y - this.dragStart.y) * 0.5; // Reduce sensitivity by 50%
            newHeight = this.cropArea.height + heightChange;

            if (newHeight >= minHeight && newHeight <= maxHeight) {
                const centerY = this.cropArea.y + this.cropArea.height / 2;
                this.cropArea.height = newHeight;
                this.cropArea.y = centerY - this.cropArea.height / 2;

                // Update drag start to prevent accumulation
                this.dragStart.y = y;
            }
        }

        // Keep crop area within canvas bounds
        this.cropArea.y = Math.max(0, Math.min(this.cropArea.y, canvas.height - this.cropArea.height));
    }

    private resizeWidth(x: number) {
        const canvas = this.canvas.nativeElement;
        const minWidth = 50; // Minimum crop area width

        // Calculate scaled image width to use as maximum
        if (this.cachedScale === 0) {
            const imageWidth = this.image.width;
            const imageHeight = this.image.height;
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const scaleX = canvasWidth / imageWidth;
            const scaleY = canvasHeight / imageHeight;
            this.cachedScale = Math.min(scaleX, scaleY);
            this.cachedScaledWidth = imageWidth * this.cachedScale;
        }

        // Maximum is the scaled image width, but not exceeding canvas width
        const maxWidth = Math.min(this.cachedScaledWidth, canvas.width);

        let newWidth: number;

        if (this.resizeHandle === 'left') {
            // Resize from left - adjust width and move position to keep center
            const widthChange = (this.dragStart.x - x) * 0.5; // Reduce sensitivity by 50%
            newWidth = this.cropArea.width + widthChange;

            if (newWidth >= minWidth && newWidth <= maxWidth) {
                const centerX = this.cropArea.x + this.cropArea.width / 2;
                this.cropArea.width = newWidth;
                this.cropArea.x = centerX - this.cropArea.width / 2;

                // Update drag start to prevent accumulation
                this.dragStart.x = x;
            }
        } else if (this.resizeHandle === 'right') {
            // Resize from right - adjust width only
            const widthChange = (x - this.dragStart.x) * 0.5; // Reduce sensitivity by 50%
            newWidth = this.cropArea.width + widthChange;

            if (newWidth >= minWidth && newWidth <= maxWidth) {
                const centerX = this.cropArea.x + this.cropArea.width / 2;
                this.cropArea.width = newWidth;
                this.cropArea.x = centerX - this.cropArea.width / 2;

                // Update drag start to prevent accumulation
                this.dragStart.x = x;
            }
        }

        // Keep crop area within canvas bounds
        this.cropArea.x = Math.max(0, Math.min(this.cropArea.x, canvas.width - this.cropArea.width));
    }


    getCanvasClass(): string {
        if (this.isResizingHeight) {
            return `cropper-canvas resizing-height-${this.resizeHandle}`;
        }
        if (this.isResizingWidth) {
            return `cropper-canvas resizing-width-${this.resizeHandle}`;
        }
        if (this.isDragging) {
            return 'cropper-canvas panning';
        }
        return 'cropper-canvas pan';
    }


    cropImage() {
        if (!this.canvas?.nativeElement || !this.imageLoaded) return;

        const canvas = this.canvas.nativeElement;
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        if (!tempCtx) {
            console.error('Failed to get canvas context for cropping');
            return;
        }

        // Use cached scale or calculate if not cached
        if (this.cachedScale === 0) {
            const imageWidth = this.image.width;
            const imageHeight = this.image.height;
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const scaleX = canvasWidth / imageWidth;
            const scaleY = canvasHeight / imageHeight;
            this.cachedScale = Math.min(scaleX, scaleY);
            this.cachedScaledWidth = imageWidth * this.cachedScale;
            this.cachedScaledHeight = imageHeight * this.cachedScale;
        }

        const scaledWidth = this.cachedScaledWidth;
        const scaledHeight = this.cachedScaledHeight;
        const scale = this.cachedScale;
        const imageWidth = this.image.width;
        const imageHeight = this.image.height;
        const x = (canvas.width - scaledWidth) / 2;
        const y = (canvas.height - scaledHeight) / 2;

        // Calculate source coordinates in original image pixels (not canvas pixels)
        let sourceX = (this.cropArea.x - this.imagePosition.x) / scale;
        let sourceY = (this.cropArea.y - this.imagePosition.y) / scale;
        let sourceWidth = this.cropArea.width / scale;
        let sourceHeight = this.cropArea.height / scale;

        // Ensure source coordinates are within image bounds
        sourceX = Math.max(0, Math.min(sourceX, imageWidth));
        sourceY = Math.max(0, Math.min(sourceY, imageHeight));
        sourceWidth = Math.min(sourceWidth, imageWidth - sourceX);
        sourceHeight = Math.min(sourceHeight, imageHeight - sourceY);

        // Set tempCanvas to actual pixel dimensions from original image (preserve quality)
        // This ensures we maintain the original resolution instead of canvas resolution
        tempCanvas.width = sourceWidth;
        tempCanvas.height = sourceHeight;

        // Enable high-quality image smoothing for better crop quality
        tempCtx.imageSmoothingEnabled = true;
        tempCtx.imageSmoothingQuality = 'high';

        // Apply rotation transformation to the temporary canvas
        if (this.rotationAngle !== 0) {
            const centerX = tempCanvas.width / 2;
            const centerY = tempCanvas.height / 2;
            tempCtx.translate(centerX, centerY);
            tempCtx.rotate((this.rotationAngle * Math.PI) / 180);
            tempCtx.translate(-centerX, -centerY);
        }

        // Draw the cropped portion at full resolution (1:1 scale from original image)
        tempCtx.drawImage(
            this.image,
            sourceX, sourceY, sourceWidth, sourceHeight,
            0, 0, sourceWidth, sourceHeight
        );

        // Convert to data URL with high quality (PNG preserves quality better than JPEG)
        const dataURL = tempCanvas.toDataURL('image/png');

        // Clean up temporary canvas
        tempCanvas.width = 0;
        tempCanvas.height = 0;

        this.previewImageUrl = dataURL;
        this.showPreview = true;
        this.triggerHapticFeedback();
    }

    acceptPreview() {
        // Emit the cropped image and close preview
        this.croppedImageUrl.emit(this.previewImageUrl);
        this.showPreview = false;
        this.previewImageUrl = '';
        this.triggerHapticFeedback();
    }

    onCancel() {
        this.showPreview = false;
        this.previewImageUrl = '';
        this.cancel.emit();
    }

    onConfirm() {
        if (this.showPreview) {
            this.acceptPreview();
        } else {
            this.cropImage();
        }
    }

    rejectPreview() {
        // Go back to cropping mode
        this.showPreview = false;
        this.previewImageUrl = '';
        this.triggerHapticFeedback();

        // Reinitialize canvas when returning from preview mode
        // Wait for Angular to recreate the canvas element in the DOM
        setTimeout(() => {
            // Use requestAnimationFrame to ensure DOM is fully rendered
            requestAnimationFrame(() => {
                if (this.canvas?.nativeElement && this.imageLoaded && this.image.complete) {
                    // Reinitialize the canvas context (canvas element is recreated by *ngIf)
                    this.ctx = this.canvas.nativeElement.getContext('2d');

                    if (!this.ctx) {
                        console.error('Failed to get canvas context');
                        return;
                    }

                    // Re-enable high-quality image smoothing
                    this.ctx.imageSmoothingEnabled = true;
                    this.ctx.imageSmoothingQuality = 'high';

                    // Setup canvas dimensions (preserve existing crop area and image position)
                    this.setupCanvas();

                    // Only recalculate if crop area is invalid (zero or negative)
                    if (this.cropArea.width <= 0 || this.cropArea.height <= 0) {
                        this.fitImageToCanvas();
                    }

                    // Redraw the image with existing positions
                    this.draw();
                }
            });
        }, 150);
    }

    ngOnDestroy() {
        // Clean up animation frame on component destroy
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }
}
